import { ThemeProvider } from "@emotion/react";
import { createTheme } from "@mui/material";
import { useDeviceTheme } from "hooks/useDeviceTheme";
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";

export type Theme = "dark" | "light" | "auto";

export interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

export const CustomThemeContext = createContext<ThemeState>({
  theme: "auto",
  setTheme: (theme: Theme) => {},
});

export interface CustomThemeProviderProps {
  children: ReactNode;
}

const themes = {
  light: createTheme({
    palette: {
      mode: "light",
      primary: {
        main: "#fb5f2e",
      },
      secondary: {
        main: "#42a5f5",
      },
      background: {
        default: "#e8e5e3",
      },
    },
  }),
  dark: createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: "#fb5f2e",
      },
      secondary: {
        main: "#42a5f5",
      },
      background: {
        default: "#000",
        paper: "#141414",
      },
    },
  }),
};

export const CustomThemeProvider: FC<CustomThemeProviderProps> = ({
  children,
}) => {
  const [currentTheme, setCurrentTheme] = useState<Theme>(
    (localStorage.getItem("theme") ?? "auto") as Theme
  );

  const { theme: deviceTheme } = useDeviceTheme();

  const setTheme = useCallback((theme: Theme) => {
    localStorage.setItem("theme", theme);
    setCurrentTheme(theme);
  }, []);

  const contextValue = useMemo<ThemeState>(
    () => ({
      theme: currentTheme,
      setTheme: setTheme,
    }),
    [currentTheme, setTheme]
  );

  const appliedTheme = useMemo(() => {
    if (currentTheme === "auto") {
      return themes[deviceTheme];
    }

    if (!["light", "dark"].includes(currentTheme)) {
      console.log("currentTheme", "light");
      return themes["light"];
    }

    return themes[currentTheme];
  }, [currentTheme, deviceTheme]);

  return (
    <CustomThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={appliedTheme}>{children}</ThemeProvider>
    </CustomThemeContext.Provider>
  );
};
